import {
	SET_INITIALIZED,
	ADD_EVENT,
	CLEAR_EVENTS,
	SCHEDULE_BATCH,
	REMOVE_TIMEOUT,
	SET_OPTIONS,
	SET_LISTENER,
	SET_TOKEN,
	SET_DEVICE_ID,
	SET_SOURCE_APPLICATION,
	SET_LOGGING_API,
	SET_LOGGING_ENABLED,
	SET_SEND_BATCH_IN_PROGRESS
} from './logging-mutation-types';
import cookies from 'vue-cookies';
import { v4 as uuidv4 } from 'uuid';
import { parseBool } from '@/utils/parse-functions';
import { useConsoleEventLogging, eventLevels } from './console-proxy';
import { getQueryStringAsObj } from '@/utils/get-querystring-as-obj';

const deviceIdCookieName = 'vue2did';

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'logging',
	namespaced: true,
	state: {
		initialized: false,
		batchFrequency: 60000,
		options: {},
		batchedEvents: [],
		sendBatchTimeoutId: undefined,
		userToken: undefined,
		deviceId: cookies.get(deviceIdCookieName),
		deviceIdCookieName: deviceIdCookieName,
		sourceApplication: undefined,
		loggingApi: undefined,
		loggingEnabled: true, //default to true so we can start batching events before logging is initialized. This will get set to false on init logging if configured to be off.
		sendBatchInProgress: false
	},
	mutations: {
		[SET_INITIALIZED](state, initialized) {
			state.initialized = initialized;
		},
		[SET_OPTIONS](state, options) {
			options = options || {};

			state.options = options;
			state.batchFrequency =
				options.batchFrequency || state.batchFrequency;
		},
		[SET_LISTENER](state, action) {
			window.removeEventListener('beforeunload', action, {
				capture: true
			});

			window.addEventListener('beforeunload', action, {
				capture: true
			});
		},
		[SET_DEVICE_ID](state) {
			let id = cookies.get(state.deviceIdCookieName);

			if (!id) {
				id = uuidv4();
			}

			cookies.set(state.deviceIdCookieName, id, '5y');
			state.deviceId = id;
		},
		[ADD_EVENT](state, event) {
			state.batchedEvents.push(event);
		},
		[CLEAR_EVENTS](state) {
			state.batchedEvents = [];
		},
		[SCHEDULE_BATCH](state, action) {
			state.sendBatchTimeoutId = setTimeout(action, state.batchFrequency);
		},
		[REMOVE_TIMEOUT](state) {
			if (state.sendBatchTimeoutId) {
				clearTimeout(state.sendBatchTimeoutId);
				state.sendBatchTimeoutId = undefined;
			}
		},
		[SET_TOKEN](state, userToken) {
			state.userToken = userToken;
		},
		[SET_SOURCE_APPLICATION](state, sourceApplication) {
			state.sourceApplication = sourceApplication;
		},
		[SET_LOGGING_API](state, loggingApi) {
			state.loggingApi = loggingApi;
		},
		[SET_LOGGING_ENABLED](state, loggingEnabled) {
			state.loggingEnabled = loggingEnabled;
		},
		[SET_SEND_BATCH_IN_PROGRESS](state, sendBatchInProgress) {
			state.sendBatchInProgress = sendBatchInProgress;
		}
	},
	actions: {
		initLoggingAction({ commit, getters, dispatch }, options) {
			let token;
			let sourceApplication;
			let loggingApi;
			let loggingEnabled;

			commit(SET_INITIALIZED, true);
			commit(SET_OPTIONS, options);
			commit(SET_DEVICE_ID);

			if (options) {
				token = options.userToken;
				sourceApplication = options.sourceApplication;
				loggingApi = options.loggingApi;
				loggingEnabled = options.loggingEnabled;
			}

			sourceApplication = sourceApplication || getters.sourceApplication;

			commit(SET_TOKEN, token);
			commit(SET_SOURCE_APPLICATION, sourceApplication);
			commit(SET_LOGGING_API, loggingApi);
			commit(SET_LOGGING_ENABLED, loggingEnabled);

			commit(REMOVE_TIMEOUT);

			if (getters.loggingEnabled) {
				commit(SET_LISTENER, async () => {
					await dispatch('finalizeBatchAction');
				});
				commit(SCHEDULE_BATCH, () => {
					dispatch('sendBatchAction');
				});
			}
		},
		initConsoleEventLoggingAction({ dispatch }, consoleLogEventLevel) {
			useConsoleEventLogging(
				consoleLogEventLevel,
				(event, eventLevel, target, args) => {
					dispatch('logEventAction', {
						data: {
							eventLevel: eventLevel, //info, warn, error
							event: event, //info, log, warn, error
							target: target, //console, window
							eventData: args
						}
					});
				}
			);
		},
		async sendBatchAction({ commit, getters, dispatch }) {
			if (getters.loggingEnabled) {
				if (getters.batchedEvents && getters.batchedEvents.length > 0) {
					commit(SET_SEND_BATCH_IN_PROGRESS, true);

					const url = `${getters.loggingApi}/events`;
					const body = JSON.stringify(getters.batchedEvents);
					const userToken = getters.userToken;

					await fetch(url, {
						method: 'POST',
						keepalive: true,
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${userToken}`
						},
						body: body
					});
				}

				commit(CLEAR_EVENTS);
				commit(SCHEDULE_BATCH, function() {
					dispatch('sendBatchAction');
				});

				commit(SET_SEND_BATCH_IN_PROGRESS, false);
			}
		},
		async finalizeBatchAction({ commit, dispatch }) {
			commit(REMOVE_TIMEOUT);
			await dispatch('sendBatchAction');
		},
		async logEventAction({ commit, getters, dispatch }, data) {
			//WARNING: Do not put any console.logs in this function.
			// It will cause an infinite loop because we've proxied console.log and are calling this from the proxy.

			if (getters.loggingEnabled) {
				//if there's a send in progress, wait for it to finish before sending sending another
				while (getters.sendBatchInProgress) {
					await new Promise(resolve => setTimeout(resolve, 1000));
				}

				if (getters.batchedEvents.length >= 10) {
					await dispatch('sendBatchAction');
				}

				let route = data.route;

				if (!route) {
					//get route info from window.location if route not passed in
					const paramsString = window.location.search;
					const paramsObj = getQueryStringAsObj(paramsString);

					route = {
						fullPath: window.location.href,
						path: window.location.pathname,
						query: paramsObj,
						referrer: document.referrer
					};
				}

				const now = new Date();
				const postBody = {
					eventLevel: data.data.eventLevel ?? eventLevels.info,
					event: data.data.event,
					target: data.data.target,
					eventData: {
						...data.data.eventData,
						timestamp: now.toUTCString()
					},
					deviceId: getters.deviceId,
					sourceApplication: getters.sourceApplication,
					userAgent: window.navigator.userAgent,
					route: {
						name: route.name,
						fullPath: route.fullPath,
						path: route.path,
						params: route.params,
						query: route.query
					}
				};

				commit(ADD_EVENT, postBody);
			}
		}
	},
	getters: {
		initialized: state => state.initialized,
		options: state => state.options,
		userToken: state => state.userToken,
		sendBatchTimeoutId: state => state.sendBatchTimeoutId,
		batchedEvents: state => state.batchedEvents,
		batchFrequency: state => state.batchFrequency,
		loggingEnabled: state => parseBool(state.loggingEnabled),
		deviceId: state => state.deviceId,
		sourceApplication: state => state.sourceApplication,
		loggingApi: state => state.loggingApi,
		sendBatchInProgress: state => state.sendBatchInProgress
	}
};
